import { makeStyles } from '@material-ui/core';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useErrorHandler } from 'react-error-boundary';

import { PaginatedSearchResultsGrid } from 'src/components/common/Search/PaginatedSearchResultsGrid';
import { EmptyState } from 'src/components/common/EmptyState';
import { EntityResult } from 'src/components/Entity/EntityResult';
import { renderedIdsState, selectAllState, selectedIdsState } from 'src/atoms/selectedIdsAtom';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { ZeroSearchState } from 'src/components/common/ZeroSearchState';
import { isSearchPopulated } from 'src/utils';
import { DEFAULT_LIMIT } from 'src/constants';
import { Pagination, styled } from '@mui/material';
import { useQueryEntitiesPage } from 'src/hooks/useQueryEntitiesPage';
import qs from 'qs';
import { useLocation, useNavigate } from 'react-router-dom';

interface PropTypes {
    onClick: (id: string, reason: 'drawer' | 'designConceptId') => void;
    search: App.Entities.Search;
}

const useStyles = makeStyles({
    grid: {
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    },
});

const StyledPagination = styled(Pagination)(({ theme }) => ({
    margin: `${theme.spacing(2)} auto`,
    alignContent: 'center',
    width: 'fit-content',
}));

const PaginationContainer = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    width: `calc(100% + ${theme.spacing(2)})`,
    transform: `translateX(${theme.spacing(-1)})`,
    position: 'sticky',
    left: '0',
    padding: theme.spacing(4),
    bottom: theme.spacing(-3),
}));

export const EntityResults = (props: PropTypes): JSX.Element => {
    const { onClick, search } = props;
    const classes = useStyles();
    const accessToken = useRecoilValue(isAuthorizedQuery);
    const selectedIds = useRecoilValue(selectedIdsState);
    const setSelectAll = useSetRecoilState(selectAllState);
    const [renderedIds, setRenderedIds] = useRecoilState(renderedIdsState);
    const errorHandler = useErrorHandler();
    const navigate = useNavigate();
    const location = useLocation();

    const { data, page } = useQueryEntitiesPage(
        accessToken,
        search,
        {
            suspense: true,
            onError: errorHandler,
        },
    );

    // When data is queried for the first time (i.e. renderedIds in less than 50),
    // mark them as rendered with recoil
    if (data && data.results.length) {
        const dataSet = new Set<string>();
        const dataIds = data?.results.map((e) => {
            dataSet.add(e.id);
            return e.id;
        });

        if (renderedIds.length !== dataIds.length || !renderedIds.every((el) => dataSet.has(el))) {
            setRenderedIds(data?.results.map((e) => e.id));
        }
    }

    const handleAllSelectedCheck = (newArrLength: number): void => {
        if (data) {
            setSelectAll(newArrLength === data?.results.length);
        }
    };

    const handlePaginationChange = (event: React.ChangeEvent<unknown>, value: number): void => {
        event.preventDefault();
        handleAllSelectedCheck(selectedIds.length);
        navigate({
            ...location,
            search: qs.stringify({
                ...qs.parse(location.search.slice(1)),
                page: value > 1 ? value : undefined,
            }, { arrayFormat: 'repeat' }),
        });
    };

    return (
        <>
            {!isSearchPopulated(search) && (<ZeroSearchState />)}
            {isSearchPopulated(search) && !data?.results.length && (
                <EmptyState>
                    Could not find designs for the given parameters
                </EmptyState>
            )}
            {!!data?.results.length && (
                <PaginatedSearchResultsGrid
                    className={classes.grid}
                    data={data.results}
                    itemKey={(item: Models.ContentStoreApi.V3.Entity): string => `item-${item.id}`}
                >
                    {(item: Models.ContentStoreApi.V3.Entity): JSX.Element => {
                        const keywords = item._embedded && Object.values(item._embedded.keywords);
                        const tags = item._embedded && Object.values(item._embedded.tags);
                        const culturesCount = item?.cultures ? item.cultures.filter(
                            (c) => c.dTeCEnabled && c.userEnabled,
                        ).length : 0;

                        return (
                            <EntityResult
                                culturesCount={culturesCount}
                                entity={item}
                                handleAllSelectedCheck={handleAllSelectedCheck}
                                keywords={keywords}
                                tags={tags}
                                onClick={onClick}
                            />
                        );
                    }}
                </PaginatedSearchResultsGrid>
            )}
            {data?.results && data.total > DEFAULT_LIMIT && (
                <PaginationContainer>
                    <StyledPagination
                        color="primary"
                        count={Math.ceil(data.total / DEFAULT_LIMIT)}
                        page={page}
                        onChange={handlePaginationChange}
                    />
                </PaginationContainer>
            )}
        </>
    );
};
